import { useEffect, useRef, useState } from "react";
import { copiarTexto } from "@/lib/copiar";
import { useToast } from "@/ui/Toast";

type Props = {
  /** El código tal cual se copia (folio, TE, ticket de Jimi, IMEI…). */
  valor: string;
  /** Cómo se le dice en los mensajes: "folio", "TE", "ticket de Jimi". */
  etiqueta: string;
  /** Clases del texto. El componente solo agrega el estado de "copiado". */
  className?: string;
  /** Texto a mostrar si es distinto del valor copiado (raro, pero pasa). */
  children?: React.ReactNode;
};

/**
 * Un código que se copia de un clic, en vez de tener que seleccionarlo a mano.
 *
 * Se ve como texto normal (no como botón) para no ensuciar el encabezado del
 * caso: la pista es el cursor y el subrayado punteado; que se copia de un toque
 * lo dice la Ayuda del caso (cero tooltips). Al copiar pinta un ✓ un segundo y
 * avisa por toast, que es como ya avisan los IMEIs.
 */
export default function CodigoCopiable({
  valor,
  etiqueta,
  className = "",
  children,
}: Props) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Limpiar el timer si el componente se va antes de que expire el ✓.
  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  async function copiar() {
    const ok = await copiarTexto(valor);
    if (!ok) {
      toast.error(`No pude copiar el ${etiqueta}`, "Selecciónalo y cópialo a mano");
      return;
    }
    toast.success(`${etiqueta.charAt(0).toUpperCase()}${etiqueta.slice(1)} copiado`, valor);
    setCopiado(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopiado(false), 1200);
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        // En listas la fila entera suele navegar: copiar no debe abrir nada.
        e.stopPropagation();
        void copiar();
      }}
      // text-left: un <button> centra su texto por default, y al volver copiable
      // un código largo (el folio se parte en dos líneas en pantallas angostas)
      // quedaba centrado en vez de alineado con el resto del encabezado.
      aria-label={`Copiar ${etiqueta} ${valor}`}
      className={`inline-flex items-center gap-1 text-left cursor-pointer underline decoration-dotted decoration-transparent hover:decoration-current underline-offset-2 transition ${className}`}
    >
      {children ?? valor}
      {copiado && (
        <span className="text-[10px] text-emerald-400" aria-hidden>
          ✓
        </span>
      )}
    </button>
  );
}
